import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { mergeWithUnifiedAccounts } from '@/lib/mailboxAggregations';
import { getErrorMessage } from '@/lib/error';
import type { Account } from '@/types/email';
import {
  createAccount,
  fetchAccounts,
  updateAccount,
  type CreateAccountPayload,
  type UpdateAccountPayload,
} from '@/api/accounts';

interface AccountStoreState {
  accounts: Account[];
  selectedAccountId: string | null;
  accountsLoading: boolean;
  accountsError: string | null;
  addingAccount: boolean;
  updatingAccount: boolean;
  isAuthenticated: boolean;
  token: string | null;

  actualAccounts: () => Account[];
  selectedAccount: () => Account | null;
  composeAccountId: () => string | null;

  loadAccounts: () => Promise<Account[]>;
  selectAccount: (accountId: string | null) => void;
  addAccount: (payload: CreateAccountPayload) => Promise<Account>;
  updateAccountData: (accountId: string, payload: UpdateAccountPayload) => Promise<void>;
  setAuthenticated: (isAuthenticated: boolean, token: string | null) => void;
  clearAccounts: () => void;
}

function resolveSelectedAccount(currentId: string | null, accounts: Account[]): string | null {
  if (!accounts.length) {
    return null;
  }
  if (currentId && accounts.some((account) => account.id === currentId)) {
    return currentId;
  }
  return accounts[0]?.id ?? null;
}

export const useAccountStore = create<AccountStoreState>()(
  conditionalDevtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          accounts: [],
          selectedAccountId: null,
          accountsLoading: false,
          accountsError: null,
          addingAccount: false,
          updatingAccount: false,
          isAuthenticated: false,
          token: null,

          actualAccounts: () => get().accounts.filter((account) => account.id !== UNIFIED_ACCOUNT_ID),

          selectedAccount: () => {
            const { accounts, selectedAccountId } = get();
            if (!selectedAccountId) {
              return null;
            }
            return accounts.find((account) => account.id === selectedAccountId) ?? null;
          },

          composeAccountId: () => {
            const { selectedAccountId } = get();
            const actualAccounts = get().actualAccounts();
            if (selectedAccountId && selectedAccountId !== UNIFIED_ACCOUNT_ID) {
              return selectedAccountId;
            }
            return actualAccounts[0]?.id ?? null;
          },

          loadAccounts: async () => {
            if (!get().isAuthenticated) {
              set({
                accounts: [],
                selectedAccountId: null,
                accountsLoading: false,
                accountsError: null,
              });
              return [];
            }

            set({ accountsLoading: true, accountsError: null });
            try {
              const data = await fetchAccounts();
              const merged = mergeWithUnifiedAccounts(data);
              const nextSelected = resolveSelectedAccount(get().selectedAccountId, merged);
              set({
                accounts: merged,
                selectedAccountId: nextSelected,
                accountsLoading: false,
                accountsError: null,
              });
              return merged;
            } catch (error) {
              const message = getErrorMessage(error) ?? 'Failed to load accounts';
              set({
                accountsLoading: false,
                accountsError: message,
              });
              throw error;
            }
          },

          selectAccount: (accountId) => {
            set({ selectedAccountId: accountId });
          },

          addAccount: async (payload) => {
            set({ addingAccount: true });
            try {
              const account = await createAccount(payload);
              await get().loadAccounts();
              set({
                addingAccount: false,
                selectedAccountId: account.id,
              });
              return account;
            } catch (error) {
              set({ addingAccount: false });
              throw error;
            }
          },

          updateAccountData: async (accountId, payload) => {
            set({ updatingAccount: true });
            try {
              await updateAccount(accountId, payload);
              await get().loadAccounts();
              set({ updatingAccount: false });
            } catch (error) {
              set({ updatingAccount: false });
              throw error;
            }
          },

          setAuthenticated: (isAuthenticated, token) => {
            if (!isAuthenticated) {
              set({
                isAuthenticated: false,
                token: null,
                accounts: [],
                selectedAccountId: null,
                accountsError: null,
                accountsLoading: false,
              });
              return;
            }
            set({ isAuthenticated: true, token });
          },

          clearAccounts: () => {
            set({ accounts: [], selectedAccountId: null });
          },
        }),
        {
          name: 'account-storage',
          partialize: (state) => ({
            selectedAccountId: state.selectedAccountId,
          }),
        }
      )
    ),
    { name: 'AccountStore' }
  )
);
